import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Link, goBack } from 'core/routing';
import {
  NotificationBadge,
  // Fallback,
  Heading,
  Avatar,
  NavTab,
  MenuItem,
  Menu,
  Logo,
  Icon,
  Box,
} from 'components';
import { modalStore } from 'apps/modals/app';
import { Menu as ModalMenu } from 'apps/modals/menu';

import { analytics } from 'core/analytics';
import { useClientStore, useRouterStore, useHeaderStore } from 'apps/hooks';

import Search from 'apps/components/header/blocks/search';
import { notificationCenter } from 'apps/notificationCenter/appManager';
import { NotificationCenter } from 'apps/notificationCenter';
import { frameStore } from 'apps/components/Frame/appManager';

import * as Icons from '../icons';
import { FrameHeaderStyled, FrameHeaderTitle } from './frameHeader.styled';
import FrameTab from './FrameTab';
import Revisions from './Revisions';
// import NoNotifications from './NoNotifications';
import { conditionalCommunityRoutes } from '../config';

import HeaderFileFeatures from './HeaderFileFeatures';

const baseBoxStyles = {
  display: 'flex',
  alignItems: 'center',
};
const wrapperBoxStyle = { display: 'flex', flex: 1 };

const FrameHeader = observer(() => {
  const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);
  const [notificationEl, setNotificationEl] = useState<null | HTMLElement>(
    null,
  );
  const clientStore = useClientStore();
  const routerStore = useRouterStore();
  const headerStore = useHeaderStore();

  let username = null;

  if (clientStore?.signedInProfile) {
    ({ username } = clientStore.signedInProfile);
  }

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    if (notificationEl) {
      setNotificationEl(null);
    } else {
      setNotificationEl(event.currentTarget);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    if (avatarEl) {
      setAvatarEl(null);
    } else {
      setAvatarEl(event.currentTarget);
    }
  };

  const closeProfileMenu = () => {
    setAvatarEl(null);
  };

  const closeNotificationMenu = () => {
    setNotificationEl(null);
  };

  const handlePlusClick = () => {
    modalStore.SetModalComponent(ModalMenu);
    analytics.sendEvent('clicked-main-menu');
  };

  const handleCommunityClick = () => {
    if (clientStore.atCommunityRoute) {
      goBack();
    } else {
      routerStore.navigate('home.community.featured');
    }
  };

  const handleSignOut = () => {
    clientStore.SignOutUser();
  };

  const notificationsPendingCount = notificationCenter.pendingCount;
  // const notificationsPendingCount = notificationCenter.notificationsConn.arrayApp.apps.reduce(
  //   (acc: number, notification: { read: boolean }) => {
  //     if (!notification.read) return acc + 1;
  //     return acc;
  //   },
  //   0,
  // );
  // TODO: optimize this method
  let title = clientStore.atCommunityRoute ? 'Wikifactory Community' : '';
  // if it's a conditionalCommunityRoute, wait until workspaces are loaded to actually show the title
  // otherwise the title will be shown for a moment before the workspaces are loaded and potentially
  // the system realizes it's not a community route.
  if (
    clientStore.signedInProfile &&
    conditionalCommunityRoutes.includes(routerStore.route.name) &&
    !clientStore.areWorkspacesLoaded
  ) {
    title = '';
  }

  return (
    <FrameHeaderStyled>
      <Box sx={baseBoxStyles}>
        <Box sx={{ ...wrapperBoxStyle, justifyContent: 'flex-start' }}>
          <Box sx={baseBoxStyles}>
            <Box
              sx={{
                ...baseBoxStyles,
                justifyContent: 'center',
                width: 40,
                height: 32,
                flexShrink: 0,
              }}
            >
              <Link name='home'>
                <Logo size='20px' negativeVersion />
              </Link>
            </Box>
            {clientStore.atCommunityRoute ||
            routerStore.route.name === 'home.search' ? (
              <Search
                isLogged={clientStore.signedInProfile}
                searchTerm={headerStore?.searchTerm}
                isMobile={false}
                isDark={true}
              />
              ) : null}
            <FrameTab />
            <Revisions />
          </Box>
        </Box>
        {title && (
          <Box sx={{ ...wrapperBoxStyle, justifyContent: 'center' }}>
            <FrameHeaderTitle>{title}</FrameHeaderTitle>
          </Box>
        )}
        <Box sx={{ ...wrapperBoxStyle, justifyContent: 'flex-end' }}>
          <Box sx={baseBoxStyles}>
            {frameStore.file ? (
              <HeaderFileFeatures />
            ) : (
              <Box sx={baseBoxStyles} mr={2}>
                <NavTab
                  inverse
                  size='small'
                  data-test-id='new-project'
                  onClick={handlePlusClick}
                >
                  <Icon name='add' size='small' />
                </NavTab>
                <NavTab
                  size='small'
                  active={clientStore.atCommunityRoute}
                  data-for='tabsTooltip'
                  data-tip='Community'
                  data-place='right'
                  onClick={handleCommunityClick}
                >
                  <Icon name='website' />
                </NavTab>
                <NavTab
                  size='small'
                  onClick={handleNotificationClick}
                  active={Boolean(notificationEl)}
                >
                  <NotificationBadge value={notificationsPendingCount}>
                    <Icon name='notification' />
                  </NotificationBadge>
                </NavTab>
                <NavTab
                  size='small'
                  data-test-id='top-menu-user-avatar'
                  onClick={handleAvatarClick}
                  active={Boolean(avatarEl)}
                >
                  <Avatar
                    size='small'
                    src={clientStore.signedInProfile?.avatar?.url}
                    name={clientStore.signedInProfile?.username.split('@')[1]}
                  />
                </NavTab>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Menu
        open={Boolean(notificationEl)}
        anchorEl={notificationEl}
        placement='bottom-end'
        onClose={closeNotificationMenu}
      >
        {/* <Fallback icon={<NoNotifications />} label='No Notifications yet' /> */}
        <NotificationCenter visible />
      </Menu>
      <Menu
        open={Boolean(avatarEl)}
        anchorEl={avatarEl}
        onClose={closeProfileMenu}
        onClick={closeProfileMenu}
      >
        <Box p={2}>
          <Heading size='small'>{username}</Heading>
        </Box>
        <Link name='profile' params={{ profile: username }}>
          <MenuItem>
            <Box mr={2}>
              <Icons.Profile />
            </Box>
            My Profile
          </MenuItem>
        </Link>
        <Link name='home.pricing'>
          <MenuItem>
            <Box mr={2}>
              <Icons.UpgradeAccount />
            </Box>
            Upgrade Account
          </MenuItem>
        </Link>
        <Link
          name='workspace.spaceSettings.account'
          params={{ spacePath: username }}
        >
          <MenuItem>
            <Box mr={2}>
              <Icons.Settings />
            </Box>
            Settings
          </MenuItem>
        </Link>
        <MenuItem data-test-id='top-menu-sign-out' onClick={handleSignOut}>
          <Box mr={2}>
            <Icons.SignOut />
          </Box>
          Sign Out
        </MenuItem>
      </Menu>
    </FrameHeaderStyled>
  );
});

export default FrameHeader;
